import React, { useState } from 'react';
import { Box, CircularProgress, Tooltip, Typography, LinearProgress } from '@mui/material';
import { GoogleMaterialIcon } from '../ui/GoogleMaterialIcon';
import { useCommonDetailsContext } from '../../context/Common/common';
import { tokens } from '../../theme/tokens';

interface InlineDownloadProgressProps {
  isDownloading: boolean;
  progress: number;
  statusMsg: string;
  onClick: () => void;
  disabled?: boolean;
  iconName?: string;
  iconColor?: string;
}

export const InlineDownloadProgress: React.FC<InlineDownloadProgressProps> = ({
  isDownloading,
  progress,
  statusMsg,
  onClick,
  disabled = false,
  iconName = 'download',
  iconColor,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const { publishedBranding, clientScopedBranding, validateParentData } = useCommonDetailsContext();
  const activeBranding = validateParentData?.isSingleParent ? publishedBranding : (clientScopedBranding || publishedBranding);
  const primaryColor =
    iconColor || activeBranding?.primaryColor || tokens.colors.connectedPurple;
  const containerBackgroundColor = tokens.colors.offWhite;

  if (!isDownloading) {
    return (
      <Tooltip title="Download">
        <Box
          component="button"
          onClick={onClick}
          disabled={disabled}
          sx={{
            background: 'transparent',
            border: 'none',
            cursor: disabled ? 'not-allowed' : 'pointer',
            padding: '8px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: disabled ? 0.4 : 1,
            '&:hover': {
              backgroundColor: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          <GoogleMaterialIcon name={iconName as any} color={primaryColor} size={24} />
        </Box>
      </Tooltip>
    );
  }

  const tooltipContent = (
    <Box sx={{ p: 1, minWidth: 200 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.colors.offBlack }}>
          Downloading Excel
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: primaryColor }}>
          {Math.round(progress)}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 3,
            backgroundColor: primaryColor,
          },
        }}
      />
      {statusMsg && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.75rem', color: tokens.colors.offBlack }}>
          {statusMsg}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="left"
      arrow
      open={isHovered}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: containerBackgroundColor,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            border: '1px solid #e0e0e0',
            borderRadius: '10px',
            p: 0.5,
          },
        },
        arrow: {
          sx: {
            color: containerBackgroundColor,
            '&::before': {
              border: '1px solid #e0e0e0',
            },
          },
        },
      }}
    >
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '4px',
          cursor: 'pointer',
        }}
      >
        <svg width={0} height={0}>
          <defs>
            <linearGradient id="download-progress-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={primaryColor} stopOpacity="0.55" />
              <stop offset="50%" stopColor={primaryColor} stopOpacity="0.8" />
              <stop offset="100%" stopColor={primaryColor} />
            </linearGradient>
          </defs>
        </svg>

        <CircularProgress
          variant="determinate"
          value={progress}
          size={36}
          thickness={4}
          sx={{
            position: 'absolute',
            '& .MuiCircularProgress-circle': {
              stroke: 'url(#download-progress-gradient)',
              strokeLinecap: 'round',
              transition: 'stroke-dashoffset 0.3s ease',
            },
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: `color-mix(in srgb, ${primaryColor} 10%, transparent)`,
          }}
        >
          <GoogleMaterialIcon name={iconName as any} color={primaryColor} size={20} />
        </Box>
      </Box>
    </Tooltip>
  );
};
